import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import * as FiIcons from 'react-icons/fi';

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px 8px 10px;
  background: transparent;
  border-top: 1px solid rgba(255, 255, 255, 0.14);
`;

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 18px;
  font-size: 0.98rem;
  outline: none;
  background: rgba(0, 0, 0, 0.35);
  color: #f1f1f1;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.24);
  &:focus {
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
  }
  &::placeholder {
    color: rgba(255, 255, 255, 0.65);
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(78, 78, 78, 0.95) 0%, rgba(28, 28, 28, 0.95) 100%);
  color: #fff;
  font-size: 1.2rem;
  cursor: pointer;
  transition: background 0.18s, box-shadow 0.18s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
  &:hover {
    background: linear-gradient(135deg, rgba(95, 95, 95, 0.95) 0%, rgba(40, 40, 40, 0.95) 100%);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.36);
  }
  &:disabled {
    background: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
  }
`;

const FileButton = styled(Button)`
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 1rem;
  margin-right: 1px;
  width: 32px;
  height: 32px;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
  }
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background-color: rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  margin-bottom: 10px;
`;

const ConsentPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 12px;
  background-color: rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  margin-bottom: 10px;
`;

const ConsentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ConsentQuickReplyButton = styled.button`
  background-color: rgb(255, 91, 0);
  min-width: 120px;
  height: 40px;
  padding: 0 18px;
  font-size: 15px;
  font-family: Roboto, sans-serif;
  color: rgb(0, 0, 0);
  font-weight: 700;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-style: solid;
  border-width: 2px;
  border-color: rgb(158, 62, 9);
  border-radius: 20px;
  cursor: pointer;
  transition: filter 0.15s ease-in-out;

  &:hover {
    filter: brightness(0.96);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FileName = styled.span`
  font-size: 14px;
  color: #f1f1f1;
`;

const RemoveFileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background-color: #dc3545;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover {
    background-color: #c82333;
  }
`;

interface MessageInputProps {
  onSendMessage: (content: string, file?: File) => void;
  onTyping: (sessionId?: string) => void;
  onStopTyping: (sessionId?: string) => void;
  onConsentAnswer?: (answer: 'yes' | 'no') => void;
  consentQuestion?: string | null;
  consentSubmitting?: boolean;
  disabled?: boolean;
  sessionId?: string;
}

export const MessageInput: React.FC<MessageInputProps> = ({ 
  onSendMessage, 
  onTyping, 
  onStopTyping, 
  onConsentAnswer,
  consentQuestion,
  consentSubmitting = false,
  disabled,
  sessionId
}) => {
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();

  const handleSend = () => {
    if (message.trim() || selectedFile) {
      onSendMessage(message, selectedFile || undefined);
      setMessage('');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (typeof onStopTyping === 'function') {
        onStopTyping(sessionId);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
    if (typeof onTyping === 'function') {
      onTyping(sessionId);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (typeof onStopTyping === 'function') {
        onStopTyping(sessionId);
      }
    }, 1000);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const showConsentButtons = Boolean(consentQuestion && onConsentAnswer);

  return (
    <>
      {showConsentButtons && (
        <ConsentPreview>
          <FileName>{consentQuestion}</FileName>
          <ConsentActions>
            <ConsentQuickReplyButton
              onClick={() => onConsentAnswer?.('yes')}
              disabled={disabled || consentSubmitting}
              title="Ja"
            >
              Ja
            </ConsentQuickReplyButton>
            <ConsentQuickReplyButton
              onClick={() => onConsentAnswer?.('no')}
              disabled={disabled || consentSubmitting}
              title="Nee"
            >
              Nee
            </ConsentQuickReplyButton>
          </ConsentActions>
        </ConsentPreview>
      )}
      {selectedFile && (
        <FilePreview>
          <FileName>{selectedFile.name}</FileName>
          <RemoveFileButton onClick={handleRemoveFile}>
            {FiIcons.FiX?.({ size: 14, 'aria-hidden': true })}
          </RemoveFileButton>
        </FilePreview>
      )}
      <InputContainer>
        <Input
          type="text"
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
          placeholder={disabled ? 'Archiefgesprek is alleen-lezen' : 'Type je bericht...'}
          disabled={disabled}
        />
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={disabled}
        />
        <FileButton
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          title="Bijlage toevoegen"
        >
          {FiIcons.FiPaperclip?.({ size: 20, 'aria-hidden': true })}
        </FileButton>
        <Button
          onClick={handleSend}
          disabled={disabled || consentSubmitting || (!message.trim() && !selectedFile)}
          title="Bericht verzenden"
        >
          {FiIcons.FiSend?.({ size: 20, 'aria-hidden': true })}
        </Button>
      </InputContainer>
    </>
  );
}; 
